import CryptoJS from "crypto-js";

interface CyptographydataTypes {
  REQ_ENC_DEC_SALT_LENGTH: number;
  REQ_ENC_DEC_PASS_PHRASE: string;
  REQ_ENC_DEC_IV: string;
  REQ_ENC_DEC_PASS_PHRASE_EXP: string;
  REQ_ENC_DEC_IV_EXP: string;
  REQ_ENC_DEC_PASS_PHRASE_FEDERATED: string;
  REQ_ENC_DEC_IV_FEDERATED: string;
  REQ_ENC_DEC_EXPENSE_DATE?: string;
  ENABLE_DNC?: boolean;
  DNC_PAGE_URL?: string;
  ENABLE_CMF?: boolean;
  REQ_SEARCH_DATA_ENC_DEC_PASS_PHRASE?: string;
  ENABLE_MONETIZATION?: boolean;
  ENABLE_CT?: boolean;
  REQ_BFSL_ENC_DEC_PASS_PHRASE: string;
  REQ_BFSL_ENC_DEC_IV: string;
  REQ_ENC_DEC_KEY_CONN_MNGR: string;
  BFSL_ENC_DEC_KEY: string;
  BFSL_LEADS_IV: string;
  END_REQ_KEY?: string;
}

// Get key/pharse for encryption/decryption from AEM
const cryptoObject = () => {
  // variable to cache the response value
  let cryptoData: CyptographydataTypes;
  return async () => {
    // cehck if value is cached then return the value
    if (cryptoData) {
      return cryptoData;
    }
    // if not in cached varaible then make the API call
    try {
      const resp: any = await fetch("/bin/end.json");
      const data = await resp.json();
      cryptoData = decodeBtoaObject(data);
      return cryptoData;
    } catch (err) {
      // this code for local testing only, it will not run in any env because `end.json`
      // end will send the data in repsonse
      if (typeof document !== "undefined") {
        const response = document.querySelector(
          "[data-cryptographydata]",
        ) as HTMLElement;
        if (response?.dataset) {
          const data: CyptographydataTypes = JSON.parse(
            response.dataset.cryptographydata as string,
          ) as CyptographydataTypes;
          const responseData = decodeBtoaObject(data);
          cryptoData = responseData;
          return responseData;
        }
      }
    }
  };
};
export const cryptoAPICalledData =
  cryptoObject() as () => Promise<CyptographydataTypes>;

// Function for generate random Hex
export const generateRandomHex = (length: number): string => {
  const wordArray = CryptoJS.lib.WordArray.random(length);
  return CryptoJS.enc.Hex.stringify(wordArray);
};

// Generate Salt
export const generateSalt = async (): Promise<string> => {
  try {
    let responseData: CyptographydataTypes;
    responseData = await cryptoAPICalledData();
    const salt = generateRandomHex(responseData?.REQ_ENC_DEC_SALT_LENGTH);
    return salt;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

// Generate Key
export const generateKey = async (salt: string) => {
  try {
    const responseData: CyptographydataTypes = await cryptoAPICalledData();
    const passPhrase = responseData?.REQ_ENC_DEC_PASS_PHRASE;
    const key = CryptoJS.PBKDF2(passPhrase, CryptoJS.enc.Hex.parse(salt), {
      keySize: 8, // 256/32 = 8
      iterations: 1000,
    });

    return key;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const openerPromise: Record<
  string,
  {
    promise: Promise<CryptoJS.lib.WordArray> | null;
    value: CryptoJS.lib.WordArray | null;
  }
> = {};

// Function for AES 256 Encryption
export const encrypt = async (data: string, salt: string) => {
  try {
    const responseData = await cryptoAPICalledData();
    if (openerPromise[salt] === undefined) {
      openerPromise[salt] = { promise: null, value: null };
      openerPromise[salt].promise = generateKey(salt);
    }
    const key =
      openerPromise[salt].value ?? (await openerPromise[salt].promise);
    const ivKey = responseData?.REQ_ENC_DEC_IV;
    const encrypted = CryptoJS.AES.encrypt(
      data,
      key as CryptoJS.lib.WordArray,
      {
        iv: CryptoJS.enc.Hex.parse(ivKey),
      },
    );
    return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Function for  AES 256 Decryption
export const decrypt = async (data: string, salt: string) => {
  try {
    const responseData: CyptographydataTypes = await cryptoAPICalledData();
    if (openerPromise[salt] === undefined) {
      openerPromise[salt] = { promise: null, value: null };
      openerPromise[salt].promise = generateKey(salt);
    }
    const key =
      openerPromise[salt].value ?? (await openerPromise[salt].promise);
    const ivKey = responseData?.REQ_ENC_DEC_IV;

    const params = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(data),
    });
    const decrypted: CryptoJS.lib.WordArray = CryptoJS.AES.decrypt(
      params,
      key as CryptoJS.lib.WordArray,
      {
        iv: CryptoJS.enc.Hex.parse(ivKey),
      },
    );
    const decData: string = decrypted.toString(CryptoJS.enc.Utf8);
    return decData ? (JSON.parse(decData) as CryptoJS.lib.WordArray) : decData;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const encryptAESCBC256 = async (
  data: string,
  passPhrase: string,
  iv: string,
): Promise<string> => {
  const encrypted = CryptoJS.AES.encrypt(
    data,
    CryptoJS.enc.Utf8.parse(passPhrase),
    {
      keySize: 32,
      iv: CryptoJS.enc.Utf8.parse(iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    },
  );
  return encrypted.toString();
};

export const encryptAESECB128 = async (data: string, key?: string) => {
  const responseData = await cryptoAPICalledData();
  const keyutf8 = CryptoJS.enc.Utf8.parse(
    key ?? responseData?.REQ_SEARCH_DATA_ENC_DEC_PASS_PHRASE ?? "",
  );
  const encrypted = CryptoJS.AES.encrypt(data, keyutf8, {
    keySize: 16,
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  });
  return encrypted.toString();
};

export const decryptAESCBC256 = async (
  data: string,
  passPhrase: string,
  iv: string,
): Promise<string> => {
  const decrypted = CryptoJS.AES.decrypt(
    data,
    CryptoJS.enc.Utf8.parse(passPhrase),
    {
      keySize: 32,
      iv: CryptoJS.enc.Utf8.parse(iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    },
  );
  return decrypted.toString(CryptoJS.enc.Utf8);
};

export const encryptWebform = async (data: string): Promise<string> => {
  try {
    const responseData = await cryptoAPICalledData();
    return await encryptAESCBC256(
      data,
      responseData?.REQ_ENC_DEC_PASS_PHRASE_EXP,
      responseData?.REQ_ENC_DEC_IV_EXP,
    );
  } catch (e) {
    console.error(e, { reason: "Error while encrypting data for webforms" });
    throw e;
  }
};

export const encryptFederated = async (data: string): Promise<string> => {
  try {
    const responseData = await cryptoAPICalledData();
    return await encryptAESCBC256(
      data,
      responseData?.REQ_ENC_DEC_PASS_PHRASE_FEDERATED,
      responseData?.REQ_ENC_DEC_IV_FEDERATED,
    );
  } catch (e) {
    console.error(e, {
      reason: "Error while encrypting data for BM/Investement/Insurance",
    });
    throw e;
  }
};

export const decryptFederated = async (data: string): Promise<string> => {
  try {
    const responseData = await cryptoAPICalledData();
    return await decryptAESCBC256(
      data,
      responseData?.REQ_ENC_DEC_PASS_PHRASE_FEDERATED,
      responseData?.REQ_ENC_DEC_IV_FEDERATED,
    );
  } catch (e) {
    console.error(e, {
      reason: "Error while decrypting data from BM/Investement/Insurance",
    });
    throw e;
  }
};

export const decryptExpenseDate = async (): Promise<string> => {
  try {
    const responseData = await cryptoAPICalledData();
    return responseData?.REQ_ENC_DEC_EXPENSE_DATE ?? "";
  } catch (e) {
    console.error(e, { reason: "Error while extracting expense date" });
    throw e;
  }
};

export const encryptEkycBfsl = async (data: string): Promise<string> => {
  try {
    const responseData = await cryptoAPICalledData();
    return await encryptAESCBC256(
      data,
      responseData?.REQ_BFSL_ENC_DEC_PASS_PHRASE,
      responseData?.REQ_BFSL_ENC_DEC_IV,
    );
  } catch (e) {
    console.error(e, { reason: "Error while encrypting data for EKYC BFSL" });
    throw e;
  }
};

export const decodeBtoaObject = (
  encodedObject: CyptographydataTypes,
): CyptographydataTypes => {
  const decodedObject: CyptographydataTypes = {
    REQ_ENC_DEC_SALT_LENGTH: encodedObject.REQ_ENC_DEC_SALT_LENGTH,
    REQ_ENC_DEC_PASS_PHRASE: atob(encodedObject.REQ_ENC_DEC_PASS_PHRASE),
    REQ_ENC_DEC_IV: atob(encodedObject.REQ_ENC_DEC_IV),
    REQ_ENC_DEC_PASS_PHRASE_EXP: atob(
      encodedObject.REQ_ENC_DEC_PASS_PHRASE_EXP,
    ),
    REQ_ENC_DEC_IV_EXP: atob(encodedObject.REQ_ENC_DEC_IV_EXP),
    REQ_ENC_DEC_PASS_PHRASE_FEDERATED: atob(
      encodedObject.REQ_ENC_DEC_PASS_PHRASE_FEDERATED,
    ),
    REQ_ENC_DEC_IV_FEDERATED: atob(encodedObject.REQ_ENC_DEC_IV_FEDERATED),
    REQ_ENC_DEC_EXPENSE_DATE: encodedObject.REQ_ENC_DEC_EXPENSE_DATE
      ? atob(encodedObject.REQ_ENC_DEC_EXPENSE_DATE)
      : "",
    ENABLE_DNC: encodedObject.ENABLE_DNC ?? false,
    DNC_PAGE_URL: encodedObject.DNC_PAGE_URL
      ? atob(encodedObject.DNC_PAGE_URL)
      : "",
    ENABLE_CMF: encodedObject.ENABLE_CMF ?? false,
    REQ_SEARCH_DATA_ENC_DEC_PASS_PHRASE:
      encodedObject.REQ_SEARCH_DATA_ENC_DEC_PASS_PHRASE
        ? atob(encodedObject.REQ_SEARCH_DATA_ENC_DEC_PASS_PHRASE)
        : "",
    ENABLE_MONETIZATION: encodedObject.ENABLE_MONETIZATION ?? false,
    ENABLE_CT: encodedObject.ENABLE_CT ?? false,
    REQ_BFSL_ENC_DEC_PASS_PHRASE: atob(
      encodedObject.REQ_BFSL_ENC_DEC_PASS_PHRASE,
    ),
    REQ_BFSL_ENC_DEC_IV: atob(encodedObject.REQ_BFSL_ENC_DEC_IV),
    REQ_ENC_DEC_KEY_CONN_MNGR: atob(encodedObject.REQ_ENC_DEC_KEY_CONN_MNGR),
    BFSL_ENC_DEC_KEY: atob(encodedObject.BFSL_ENC_DEC_KEY),
    BFSL_LEADS_IV: atob(encodedObject.BFSL_LEADS_IV),
    END_REQ_KEY: encodedObject.END_REQ_KEY,
  };
  return decodedObject;
};

export const decryptWebform = async (data: string): Promise<string> => {
  try {
    const responseData = await cryptoAPICalledData();
    return await decryptAESCBC256(
      data,
      responseData?.REQ_ENC_DEC_PASS_PHRASE_EXP,
      responseData?.REQ_ENC_DEC_IV_EXP,
    );
  } catch (e) {
    console.error(e, {
      reason: "Error while decrypting data",
    });
    throw e;
  }
};

// decrypt mobile number set by the connection manager
export const decryptLocalStorageMobileNo = async (encryptedMobile: string) => {
  try {
    const responseData = await cryptoAPICalledData();
    const key = CryptoJS.enc.Utf8.parse(
      responseData?.REQ_ENC_DEC_KEY_CONN_MNGR,
    );
    // Backward-compatible decode:
    // - New format: base64(iv):base64(ciphertext) encrypted with AES-CBC.
    // - Legacy format: raw AES-ECB ciphertext.
    let decrypted: CryptoJS.lib.WordArray;
    if (encryptedMobile.includes(":")) {
      const [ivBase64, cipherBase64] = encryptedMobile.split(":", 2);
      if (!ivBase64 || !cipherBase64) {
        throw new Error("Invalid encrypted mobile format");
      }

      const cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Base64.parse(cipherBase64),
      });

      decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
        iv: CryptoJS.enc.Base64.parse(ivBase64),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
    } else {
      decrypted = CryptoJS.AES.decrypt(encryptedMobile, key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      });
    }

    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    return decryptedString ? JSON.parse(decryptedString) : "";
  } catch (error) {
    console.error(error, "Failed to decrypt mobile number");
    throw error;
  }
};

// encrypt mobile number for storage using the same key as decryption
export const encryptLocalStorageMobileNo = async (mobileNumber: string) => {
  try {
    const responseData = await cryptoAPICalledData();
    const key = CryptoJS.enc.Utf8.parse(
      responseData?.REQ_ENC_DEC_KEY_CONN_MNGR,
    );
    const iv = CryptoJS.lib.WordArray.random(16);
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(mobileNumber), key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return `${iv.toString(CryptoJS.enc.Base64)}:${encrypted.ciphertext.toString(CryptoJS.enc.Base64)}`;
  } catch (error) {
    console.error(error, "Failed to encrypt mobile number");
    throw error;
  }
};

export const encryptAESECB256 = async (
  data: string,
  passPhrase: string,
  iv: string,
): Promise<string> => {
  const encrypted = CryptoJS.AES.encrypt(
    data,
    CryptoJS.enc.Utf8.parse(passPhrase),
    {
      keySize: 32,
      iv: CryptoJS.enc.Utf8.parse(iv),
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    },
  );
  return encrypted.toString();
};

export const encryptLeads = async (data: string): Promise<string> => {
  try {
    const responseData = await cryptoAPICalledData();
    return await encryptAESECB256(
      data,
      responseData.BFSL_ENC_DEC_KEY,
      responseData.BFSL_LEADS_IV,
    );
  } catch (e) {
    console.error(e, { reason: "Error while encrypting data for EKYC BFSL" });
    throw e;
  }
};

/**
 * Encrypts BFSL Open Demat / Resume Journey redirect payload using AES-CBC-256.
 * Key and IV are read from env (NEXT_PUBLIC_BFSL_RESUME_ENC_KEY / _IV).
 */
export const encryptBfslResumePayload = async (
  data: string,
): Promise<string> => {
  try {
    const key = process.env.NEXT_PUBLIC_BFSL_RESUME_ENC_KEY ?? "";
    const iv = process.env.NEXT_PUBLIC_BFSL_RESUME_ENC_IV ?? "";

    if (!key || !iv) {
      throw new Error("BFSL resume encryption key/IV missing");
    }

    return await encryptAESCBC256(data, key, iv);
  } catch (e) {
    console.error(e, {
      reason: "Error while encrypting BFSL resume journey payload",
    });
    throw e;
  }
};

export const encryptIndicesData = async (data: string): Promise<string> => {
  try {
    const responseData = await cryptoAPICalledData();
    const salt = generateRandomHex(responseData.REQ_ENC_DEC_SALT_LENGTH);
    const key = CryptoJS.PBKDF2(
      responseData.REQ_ENC_DEC_PASS_PHRASE,
      CryptoJS.enc.Hex.parse(salt),
      {
        keySize: 8,
        iterations: 1000,
      },
    );
    const encrypted = CryptoJS.AES.encrypt(data, key, {
      iv: CryptoJS.enc.Hex.parse(responseData.REQ_ENC_DEC_IV),
    });
    return salt + encrypted.ciphertext.toString(CryptoJS.enc.Base64);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getEndReqKey = async (): Promise<string> => {
  const responseData = await cryptoAPICalledData();
  return responseData?.END_REQ_KEY ?? "";
};

export const encryptPortfolioData = async (data: object): Promise<string> => {
  try {
    const responseData = await cryptoAPICalledData();
    return await encryptAESECB256(
      JSON.stringify(data),
      responseData.BFSL_ENC_DEC_KEY,
      "",
    );
  } catch (e) {
    console.error(e, { reason: "Error while encrypting data for Portfolio" });
    throw e;
  }
};
